/*
 * NWF-202: YouTube and Vimeo load only after marketing consent (Shopify Customer Privacy API).
 * "Cookies akzeptieren" grants marketing, other categories stay as the visitor set them.
 * Withdrawing consent after a video loaded reloads the page.
 * NWF-213: Google Maps embeds (`iframe[data-nwf-map]`) use the same gate.
 */
(() => {
    'use strict';

    if (window.NwfVideoConsent) return;

    const listeners = new Set();
    let allowed = false;

    function readApi() {
        const api = window.Shopify && window.Shopify.customerPrivacy;
        return !!(api && typeof api.marketingAllowed === 'function' && api.marketingAllowed());
    }

    let released = false;

    function setAllowed(value) {
        if (value === allowed) return;
        // Withdrawn after videos were loaded: a reload is the only reliable way to stop
        // running players and API scripts.
        if (!value && released) {
            window.location.reload();
            return;
        }
        allowed = value;
        listeners.forEach((callback) => {
            try {
                callback(allowed);
            } catch (error) {
                console.error(error);
            }
        });
    }

    function onChange(callback) {
        listeners.add(callback);
        return () => listeners.delete(callback);
    }

    function loadApi() {
        if (window.Shopify && window.Shopify.customerPrivacy) {
            setAllowed(readApi());
            return;
        }
        if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') return;
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
            if (!error) setAllowed(readApi());
        });
    }

    document.addEventListener('visitorConsentCollected', (event) => {
        const detail = event.detail || {};
        setAllowed(typeof detail.marketingAllowed === 'boolean' ? detail.marketingAllowed : readApi());
    });

    function providerOf(url) {
        if (typeof url !== 'string') return null;
        if (/(^|\/\/|\.)vimeo\.com\//i.test(url)) return 'vimeo';
        if (/(^|\/\/|\.)(youtube\.com|youtube-nocookie\.com|youtu\.be)\//i.test(url)) return 'youtube';
        return null;
    }

    function normalizeUrl(url) {
        if (typeof url !== 'string') return null;
        let value = url.trim();
        if (value.startsWith('//')) value = 'https:' + value;
        value = value.replace(/^http:\/\//i, 'https://');
        try {
            const parsed = new URL(value);
            if (parsed.protocol !== 'https:' || !providerOf(parsed.href)) return null;
            return parsed.href;
        } catch (error) {
            return null;
        }
    }

    function toNoCookie(url) {
        try {
            const parsed = new URL(url);
            const host = parsed.hostname.replace(/^www\./, '').replace(/^m\./, '');
            if (host === 'youtu.be') {
                parsed.pathname = '/embed' + parsed.pathname;
            } else if (host !== 'youtube.com') {
                return url;
            }
            parsed.hostname = 'www.youtube-nocookie.com';
            return parsed.href;
        } catch (error) {
            return url;
        }
    }

    function withAutoplay(url) {
        try {
            const parsed = new URL(url);
            parsed.searchParams.set('autoplay', '1');
            return parsed.href;
        } catch (error) {
            return url;
        }
    }

    function grantMarketing() {
        const api = window.Shopify && window.Shopify.customerPrivacy;
        if (!api || typeof api.setTrackingConsent !== 'function') return Promise.resolve(false);

        const consent = { marketing: true };
        const current = typeof api.currentVisitorConsent === 'function' ? api.currentVisitorConsent() || {} : {};
        ['analytics', 'preferences'].forEach((key) => {
            if (current[key] === 'yes') consent[key] = true;
            if (current[key] === 'no') consent[key] = false;
        });

        return new Promise((resolve) => {
            try {
                api.setTrackingConsent(consent, (result) => {
                    if (result && result.error) {
                        resolve(false);
                        return;
                    }
                    setAllowed(readApi());
                    resolve(allowed);
                });
            } catch (error) {
                resolve(false);
            }
        });
    }

    function canShowPreferences() {
        return !!(window.privacyBanner && typeof window.privacyBanner.showPreferences === 'function');
    }

    function createOverlay(provider, options) {
        const template = document.getElementById('NwfVideoConsentTemplate');
        if (!template || !template.content.firstElementChild) return null;

        const overlay = template.content.firstElementChild.cloneNode(true);
        overlay.querySelectorAll('[data-nwf-consent-provider]').forEach((element) => {
            if (element.getAttribute('data-nwf-consent-provider') !== provider) element.remove();
        });
        const label = template.getAttribute('data-label-' + provider);
        if (label) overlay.setAttribute('aria-label', label);
        if (!options.image) overlay.querySelector('.nwf-video-consent__image')?.remove();
        if (options.compact) overlay.classList.add('nwf-video-consent--compact');

        return overlay;
    }

    /** Shows the overlay in host until consent, then calls onLoad(byClick). Returns cancel. */
    function gate(host, provider, onLoad, options = {}) {
        if (allowed) {
            released = true;
            onLoad(false);
            return () => {};
        }

        const overlay = createOverlay(provider, options);
        if (!overlay || !host) return () => {};

        let settled = false;
        let unsubscribe = () => {};

        const cleanup = () => {
            settled = true;
            unsubscribe();
            overlay.remove();
            host.classList.remove('nwf-video-consent-host');
        };

        const release = (byClick) => {
            if (settled) return;
            cleanup();
            released = true;
            onLoad(byClick);
        };

        let clicked = false;

        host.classList.add('nwf-video-consent-host');
        host.appendChild(overlay);
        const button = overlay.querySelector('[data-nwf-consent-load]');
        button?.addEventListener('click', () => {
            if (settled || clicked) return;
            clicked = true;
            button.disabled = true;
            grantMarketing().then((granted) => {
                // Nothing loads without confirmed consent; the button can be used again.
                if (!granted && !settled) {
                    clicked = false;
                    button.disabled = false;
                }
            });
        });
        unsubscribe = onChange((value) => {
            if (value) release(clicked);
        });

        return () => {
            if (!settled) cleanup();
        };
    }

    function guardIframe(iframe) {
        if (iframe.hasAttribute('data-nwf-video-guarded')) return;
        iframe.setAttribute('data-nwf-video-guarded', '');

        const normalized = normalizeUrl(iframe.getAttribute('data-nwf-video'));
        const provider = providerOf(normalized);
        if (!normalized || !provider) return;
        const url = provider === 'youtube' ? toNoCookie(normalized) : normalized;

        const frame = document.createElement('div');
        frame.className = 'nwf-video-consent-frame';
        const width = parseInt(iframe.getAttribute('width'), 10);
        const height = parseInt(iframe.getAttribute('height'), 10);
        if (width > 0 && height > 0) {
            frame.style.maxWidth = width + 'px';
            frame.style.aspectRatio = width + ' / ' + height;
        }
        iframe.replaceWith(frame);
        frame.appendChild(iframe);

        const template = document.getElementById('NwfVideoConsentTemplate');
        if (!iframe.title && template) iframe.title = template.getAttribute('data-frame-title') || '';

        gate(
            frame,
            provider,
            (byClick) => {
                if (!iframe.hasAttribute('allow')) {
                    iframe.setAttribute('allow', 'autoplay; encrypted-media; fullscreen; picture-in-picture');
                }
                iframe.src = byClick ? withAutoplay(url) : url;
                iframe.removeAttribute('data-nwf-video');
                if (byClick) iframe.focus();
            },
            { image: true },
        );
    }

    /** Accepts Google Maps embed URLs only (www.google.<tld>/maps…, maps.google.<tld>…). */
    function mapUrl(url) {
        if (typeof url !== 'string') return null;
        let value = url.trim();
        if (value.startsWith('//')) value = 'https:' + value;
        value = value.replace(/^http:\/\//i, 'https://');
        try {
            const parsed = new URL(value);
            if (parsed.protocol !== 'https:') return null;
            const isGoogle = /^(www\.|maps\.)?google\.[a-z]{2,3}(\.[a-z]{2})?$/i.test(parsed.hostname);
            const isMaps = parsed.hostname.startsWith('maps.') || parsed.pathname.startsWith('/maps');
            return isGoogle && isMaps ? parsed.href : null;
        } catch (error) {
            return null;
        }
    }

    function guardMap(iframe) {
        if (iframe.hasAttribute('data-nwf-map-guarded')) return;
        iframe.setAttribute('data-nwf-map-guarded', '');

        // Anything that is not a Google Maps URL stays unloaded.
        const url = mapUrl(iframe.getAttribute('data-nwf-map'));
        if (!url) return;

        // The parked frame is empty; keep it out of the tab order so the overlay button comes first.
        iframe.setAttribute('tabindex', '-1');

        const host = iframe.closest('.nwf-map__frame') || iframe.parentElement;
        gate(
            host,
            'google_maps',
            (byClick) => {
                iframe.src = url;
                iframe.removeAttribute('data-nwf-map');
                iframe.removeAttribute('tabindex');
                if (byClick) iframe.focus();
            },
            { image: false },
        );
    }

    function revealImage(image) {
        const url = (image.getAttribute('data-nwf-video-img') || '')
            .trim()
            .replace(/^\/\//, 'https://')
            .replace(/^http:\/\//i, 'https://');
        const isThumbHost = /^https:\/\/(img\.youtube\.com|i\.ytimg\.com|i\.vimeocdn\.com)\//i.test(url);
        image.removeAttribute('data-nwf-video-img');
        if (!isThumbHost) return;
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
        image.classList.remove('nwf-video-placeholder');
        image.src = url;
        released = true;
    }

    function guardVideoElement(element) {
        if (element.hasAttribute('data-nwf-consent-guarded')) return;
        element.setAttribute('data-nwf-consent-guarded', '');

        const provider = element.getAttribute('source');
        if (provider !== 'youtube' && provider !== 'vimeo') return;

        const autoplay = element.hasAttribute('data-nwf-consent-autoplay');
        const poster = element.querySelector('[id^="DeferredPoster-"]');

        gate(
            element,
            provider,
            (byClick) => {
                element.removeAttribute('data-nwf-consent');
                if (autoplay) {
                    // Mirrors the in view handling of the theme's VideoElement constructor.
                    element.setAttribute('autoplay', '');
                    const inView = window.FoxTheme && window.FoxTheme.Motion && window.FoxTheme.Motion.inView;
                    if (typeof inView === 'function') {
                        inView(element, () => {
                            if (!element.paused) element.play();
                            return () => element.pause();
                        });
                    } else if (typeof element.play === 'function') {
                        element.play();
                    }
                } else if (byClick && typeof element.play === 'function') {
                    element.play();
                }
            },
            { compact: autoplay && !poster },
        );
    }

    // Runs before the theme's poster click handler.
    document.addEventListener(
        'click',
        (event) => {
            const poster = event.target.closest && event.target.closest('[id^="DeferredPoster-"]');
            const element = poster && poster.closest('video-element[data-nwf-consent]');
            if (!element) return;
            event.preventDefault();
            event.stopImmediatePropagation();
            element.querySelector('[data-nwf-consent-load]')?.focus();
        },
        true,
    );

    // Cookie settings links open the banner preferences in place when possible.
    document.addEventListener('click', (event) => {
        const link = event.target.closest && event.target.closest('a[href*="#shopifyReshowConsentBanner"]');
        if (!link || !canShowPreferences()) return;
        event.preventDefault();
        window.privacyBanner.showPreferences();
    });

    const SELECTORS = {
        iframe: 'iframe[data-nwf-video]',
        image: 'img[data-nwf-video-img]',
        player: 'video-element[data-nwf-consent]',
        map: 'iframe[data-nwf-map]',
    };

    function scan(root) {
        const within = (selector) => {
            const found = Array.from(root.querySelectorAll ? root.querySelectorAll(selector) : []);
            if (root.matches && root.matches(selector)) found.unshift(root);
            return found;
        };
        within(SELECTORS.iframe).forEach(guardIframe);
        within(SELECTORS.player).forEach(guardVideoElement);
        within(SELECTORS.map).forEach(guardMap);
        if (allowed) within(SELECTORS.image).forEach(revealImage);
    }

    onChange((value) => {
        if (value) document.querySelectorAll(SELECTORS.image).forEach(revealImage);
    });

    function init() {
        loadApi();
        scan(document);
        new MutationObserver((records) => {
            records.forEach((record) => {
                record.addedNodes.forEach((node) => {
                    if (node.nodeType === Node.ELEMENT_NODE) scan(node);
                });
            });
        }).observe(document.documentElement, { childList: true, subtree: true });
    }

    window.NwfVideoConsent = {
        allowed: () => allowed,
        onChange,
        gate,
        providerOf,
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
